import React, {
  forwardRef, useContext, useMemo, useRef, useState, useCallback, useEffect, ChangeEvent, MutableRefObject,
} from 'react';

import { WithTranslation } from 'react-i18next';
import DataFormat from '../../format/DataFormat';
import ColorSchemeContext from '../../colorScheme/ColorSchemeContext';
import COLOR_SCHEMES from '../../../constants/COLOR_SCHEMES';

import { StyledInputWrapper, StyledInput, StyledCounter } from './Textarea.styles';
import FormColContext from '../_FormColContext';
import { InputProps } from '../_Input';

export interface TextareaProps extends InputProps<HTMLTextAreaElement> {
  /** Ref for the input */
  inputRef?: MutableRefObject<HTMLTextAreaElement>;
  /** Specifies the width of the input itself. */
  inputWidth?: number;
  /** @ignore */
  textOnly?: boolean;
  /** Maximum number of characters */
  maxLength?: number;
  /** Hides the character counters */
  hideCounter?: boolean;
  /** Override the min-height of the field. */
  minHeight?: number;
}

const TextareaDefaultProps = {
  hideCounter: false,
  maxLength: 5000,
  minHeight: 80,
};

/**
 * @mode developer
 */
const Textarea = Object.assign(
  forwardRef<HTMLTextAreaElement, TextareaProps & WithTranslation>(({
    t, tReady, inputRef, inputWidth, readOnly, disabled, textOnly, value, maxLength, minHeight, hideCounter, onChange, autoFocus, ...props
  }, ref) => {
    const { colId } = useContext(FormColContext);

    const { scheme } = useContext(ColorSchemeContext);
    const customRef = useRef<HTMLTextAreaElement>();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const resolvedRef = useMemo(() => (inputRef || ref || customRef) as MutableRefObject<HTMLTextAreaElement>, []);

    const [charCount, setCharCount] = useState(0);
    const isMaxLengthInvalid = useMemo(() => charCount > maxLength, [charCount, maxLength]);

    const tabIndexProps = useMemo(() => (readOnly) ? { tabIndex: -1 } : {}, [readOnly]);

    const updateHeight = useCallback(() => {
      if (resolvedRef.current) {
        resolvedRef.current.style.height = '1px';
        resolvedRef.current.style.height = `${(resolvedRef?.current?.scrollHeight || 0) + 2}px`;
      }
    }, [resolvedRef]);

    const updateCharCount = useCallback(() => {
      if (resolvedRef.current) {
        setCharCount(resolvedRef.current?.value.length);
      }
    }, [resolvedRef]);

    const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
      updateHeight();
      updateCharCount();
      if (onChange) onChange(event);
    }, [onChange, updateHeight, updateCharCount]);

    useEffect(() => {
      updateHeight();
      updateCharCount();
    }, [value, updateHeight, updateCharCount]);

    useEffect(() => {
      if (autoFocus) resolvedRef?.current.focus();
    }, [autoFocus, resolvedRef]);

    if (textOnly) {
      return <DataFormat>{value}</DataFormat>;
    }

    return (
      <StyledInputWrapper inputWidth={inputWidth}>
        <StyledInput
          tag="textarea"
          shaded={scheme === COLOR_SCHEMES.SHADE}
          data-testid="Textarea"
          ref={resolvedRef}
          disabled={disabled}
          readOnly={readOnly}
          value={value}
          hideCounter={hideCounter}
          minHeight={minHeight}
          onChange={handleChange}
          aria-labelledby={colId}
          {...props}
          {...tabIndexProps}
        />
        {!hideCounter && (
          <StyledCounter isMaxLengthInvalid={isMaxLengthInvalid}>
            {charCount} / {maxLength} {t('characters')}
          </StyledCounter>
        )}
      </StyledInputWrapper>
    );
  }),
  {
    defaultProps: TextareaDefaultProps,
    labeledControl: true,
  },
);

export default Textarea;
